import styled, { keyframes } from "styled-components";
import { useState, useEffect } from "react";
import { StyledButton, StyledBorderButton } from "@components";
import { theme, breakpoint } from "@utilities";
import { ICustomerDetail } from "@interfaces";
import { IPaymentReceiver } from "src/Interfaces/paymentReceiver";
import { quoteApi } from "@api";
import { ConfirmPaymentLinkModal } from "./ConfirmPaymentLinkModal";
import { CreateNewTravelerModal } from "./CreateNewTravelerModal";
import { ErrorToast } from "src/Components/toasts/ErrorToast";

interface SendPaymentLinkModalProps {
    customers: ICustomerDetail[];
    quoteId?: string;
    selectedTraveler: IPaymentReceiver | null;
    setSelectedTraveler: (traveler: IPaymentReceiver | null) => void;
    isEmailing: boolean;
    setIsEmailing: (isEmailing: boolean) => void;
    isOrderInProgress: boolean;
    handleSendLink: () => Promise<void>;
    handleResend: (travelerId: number) => Promise<void>;
    onClose: () => void;
}

interface StyledSuggestionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    isSuggestionClicked: boolean;
    disabled?: boolean;
}

export const SendPaymentLinkModal = ({
    customers,
    quoteId,
    selectedTraveler,
    setSelectedTraveler,
    isOrderInProgress,
    handleSendLink,
    onClose,
}: SendPaymentLinkModalProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [paymentReceivers, setPaymentReceivers] = useState<IPaymentReceiver[]>([]);

    const mergedReceivers = (() => {
        const seen = new Set(paymentReceivers.map((r) => r.email.toLowerCase()));
        const fromCustomers: IPaymentReceiver[] = [];
        (customers || []).forEach((c) => {
            const email = c.customer?.email || c.guest?.email;
            if (!email || seen.has(email.toLowerCase())) return;
            seen.add(email.toLowerCase());
            const firstName = c.customer?.first_name || c.guest?.first_name || "";
            const lastName = c.customer?.last_name || c.guest?.last_name || "";
            fromCustomers.push({
                name: c.name || `${firstName} ${lastName}`.trim(),
                email,
                redeemer_id: c.redeemer_id,
            });
        });
        return [...paymentReceivers, ...fromCustomers];
    })();

    const filteredTravelers = mergedReceivers.filter((traveler) =>
        `${traveler.name} ${traveler.email}`.toLowerCase().includes(searchQuery.toLowerCase()),
    );

    const fetchPaymentReceivers = async () => {
        if (!quoteId) return;
        try {
            const response = await quoteApi.getPaymentReceivers(quoteId);
            setPaymentReceivers(response.data.data);
            setSelectedTraveler(null);
        } catch (error) {
            console.error("Failed to fetch payment receivers:", error);
            ErrorToast({ Message: "Failed to update traveler list" });
        }
    };

    useEffect(() => {
        fetchPaymentReceivers();
    }, [quoteId]);

    const handleSendClick = () => {
        if (!selectedTraveler) {
            ErrorToast({ Message: "Select a traveler" });
            return;
        }
        setShowConfirmModal(true);
    };

    return (
        <>
            {!showConfirmModal && (
                <>
                    <Overlay onClick={onClose} />
                    <BottomSheet onClick={(e) => e.stopPropagation()}>
                        <div className="details-container">
                            <div className="header">
                                <h2>Send Payment Link</h2>
                                <CloseButton onClick={onClose}>
                                    <svg
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M18 6L6 18M6 6L18 18"
                                            stroke="#080914"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </CloseButton>
                            </div>

                            <TravelerSearchInput
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Tap here to search"
                            />
                            <ScrollBox>
                                {filteredTravelers.map((traveler) => (
                                    <div className="details" key={traveler.email + traveler.name}>
                                        <span>
                                            <StyledSuggestionButton
                                                type="button"
                                                isSuggestionClicked={
                                                    selectedTraveler?.email.toLowerCase() === traveler.email.toLowerCase()
                                                }
                                                onClick={() => setSelectedTraveler(traveler)}
                                            >
                                                <div />
                                            </StyledSuggestionButton>
                                        </span>
                                        <div>
                                            <h1>{traveler.name}</h1>
                                            <p>{traveler.email}</p>
                                        </div>
                                    </div>
                                ))}
                                {filteredTravelers.length === 0 && (
                                    <p style={{ color: "#6e7375", fontSize: "14px" }}>
                                        No travelers found
                                    </p>
                                )}
                            </ScrollBox>
                        </div>

                        <div className="styled-buttons">
                            <BorderButton
                                $borderRadius={theme.borderRadius.small}
                                color="transparent"
                                $textColor={theme.colors.button_text1}
                                onClick={() => setShowCreateModal(true)}
                            >
                                Create new
                            </BorderButton>
                            <NormalStyledButton
                                $borderRadius={theme.borderRadius.small}
                                color={theme.colors.main}
                                onClick={handleSendClick}
                                disabled={isOrderInProgress || !selectedTraveler}
                            >
                                {isOrderInProgress ? "Sending..." : "Send Link"}
                            </NormalStyledButton>
                        </div>
                    </BottomSheet>
                </>
            )}

            {showConfirmModal && selectedTraveler && (
                <ConfirmPaymentLinkModal
                    traveler={selectedTraveler}
                    onConfirm={async () => {
                        await handleSendLink();
                        await fetchPaymentReceivers();
                    }}
                    onCancel={() => {
                        setShowConfirmModal(false);
                        onClose();
                    }}
                    onCloseParent={onClose}
                />
            )}

            {showCreateModal && quoteId && (
                <CreateNewTravelerModal
                    quoteId={quoteId}
                    onClose={() => setShowCreateModal(false)}
                    onTravelerAdded={fetchPaymentReceivers}
                />
            )}
        </>
    );
};

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;

    @media (max-width: 768px) {
        min-width: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    ${breakpoint.breakTablet`
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall].map(
        (breakpointFn) => breakpointFn`
    display: block;`,
    )}
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 10;
    cursor: pointer;

    @media (max-width: 768px) {
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
    }

    ${breakpoint.breakTablet`
        display: block !important;
        visibility: visible !important;
        opacity: 1 !important;
        pointer-events: auto !important;
        z-index: 10;
    `}
`;

const BottomSheet = styled.div`
    display: none;
    ${[breakpoint.breakMobile, breakpoint.breakSmall, breakpoint.breakMobileMedium].map(
        (breakpointFn) => breakpointFn`
        display: block;
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;

        .details-container{
            display: flex;
            flex-direction: column;
            margin-bottom: 30px;
            h2 {
                font-size: 20px;
                font-weight: 600;
                margin-bottom: 20px !important;
            }
            .details {
                display : flex;
                align-items : center;
                gap : 16px;
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
                div{
                    display : flex;
                    flex-direction : column;
                    flex: 1;
                    h1{
                        font-size : 24px;
                        font-weight : 600;
                        margin-bottom: 6px;
                    }
                    p{
                        font-size : 12px;
                        color : #6E7375;
                        margin-bottom: 0;
                    }
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
        .styled-buttons {
            display : flex;
            gap : 14px;
        }
    `,
    )}

    @media (max-width: 768px) {
        display: block !important;
        position: fixed !important;
        bottom: 0 !important;
        left: 0 !important;
        right: 0 !important;
        top: auto !important;
        transform: none !important;
        width: 100% !important;
        max-width: 100% !important;
        z-index: 15;
        background: #fff;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
        animation: ${slideUp} 0.3s ease-out;
        padding: 38px 16px 30px 16px;
        height: auto !important;
        min-height: auto !important;

        .details-container {
            h2 {
                margin-bottom: 20px;
            }
            .details {
                margin-bottom: 20px;
                &:last-child {
                    margin-bottom: 0;
                }
                div h1 {
                    margin-bottom: 6px;
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
    }

    ${breakpoint.breakTablet`
        display: block;
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 700px;
        max-width: 90vw;
        z-index: 15;
        background: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        padding: 50px 30px;
        animation: none;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        
        .details-container {
            display: flex;
            flex-direction: column;
            gap: 20px;
            margin-bottom: 30px;
            h2 {
                font-size: 26px;
                font-weight: 700;
                margin-bottom: 20px;
            }
            .details {
                display: flex;
                align-items: center;
                gap: 16px;
                div {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                    flex: 1;
                    h1 {
                        font-size: 24px;
                        font-weight: 600;
                    }
                    p {
                        font-size: 12px;
                        color: #6e7375;
                    }
                }
            }
            .header {
                display: flex;
                justify-content: space-between;
            }
        }
        
        .styled-buttons {
            display: flex;
            gap: 24px;
        }
    `}
`;

const TravelerSearchInput = styled.input`
    width: 100%;
    height: 48px;
    background: #f4f6f7;
    border-radius: 8px;
    border: none;
    padding: 0 16px;
    font-family: "Poppins";
    font-size: 16px;
    color: #131214;

    &::placeholder {
        color: #898d8f;
    }

    @media (max-width: 768px) {
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }
`;

const ScrollBox = styled.div`
    max-height: 20vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 20px;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 2px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
    }

    @media (max-width: 768px) {
        gap: 0;
        -webkit-overflow-scrolling: touch;
        -webkit-scrollbar-width: thin;
        -webkit-scrollbar-color: #d1d5db transparent;

        .details {
            margin-bottom: 20px;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const BorderButton = styled(StyledBorderButton)`
    margin-top: 0px;
    padding: 24px 0px;

    @media (max-width: 768px) {
        min-height: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    ${breakpoint.breakTablet`
        transition: all 0.2s ease;
        &:hover {
            transform: scale(1.02);
            opacity: 0.9;
        }
    `}
`;

const NormalStyledButton = styled(StyledButton)`
    margin-top: 0px;
    padding: 25px 0px;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    @media (max-width: 768px) {
        min-height: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    ${breakpoint.breakTablet`
        transition: all 0.2s ease;
        &:hover {
            transform: scale(1.02);
            opacity: 0.9;
        }
    `}
`;

const StyledSuggestionButton = styled.button<StyledSuggestionButtonProps>`
    width: 30px;
    height: 30px;
    padding: 6px;
    border-radius: 50px;
    border: ${(props) =>
        props.isSuggestionClicked ? `1px solid ${theme.colors.time_radio}` : "1px solid #daddde"};
    transition: background-color 0.3s ease;
    cursor: pointer;
    background-color: ${(props) => (props.isSuggestionClicked ? theme.colors.time_radio : "")};

    /* Mobile touch improvements */
    @media (max-width: 768px) {
        /* Safari mobile touch improvements */
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    div {
        width: 100%;
        height: 100%;
        border-radius: 50px;
        background-color: #fff;
    }
`;
